import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getCostTransactionChartKey } from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { useInvalidateCostQueries } from 'modules/cost/api/invalidate'

import { MEDICINE_REQUEST_CONFIG } from '.'
import {
  CreateMedicineByProductVariables,
  CreateMedicineVariables,
  CreateProductMedicalVariables,
  CreateProductMessageResponse,
  EditMedicineVariables,
  EditProductMedicineVariables,
} from './types'

export const useMutateCreateMedicine = () => {
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()
  return useMutation(
    ({ body: data }: CreateMedicineVariables) =>
      cycleApi.request({ ...MEDICINE_REQUEST_CONFIG.create, data }),
    {
      onSuccess: () => {
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateCreateMedicineName = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  return useMutation(
    (name: string) =>
      cycleApi.request({
        ...MEDICINE_REQUEST_CONFIG.createName,
        data: {
          name,
        },
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: getCostTransactionChartKey.all() })
      },
    }
  )
}

export const useMutateUpdateMedicine = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()
  return useMutation(
    ({ medicineId, body: data }: EditMedicineVariables) =>
      cycleApi.request({
        ...MEDICINE_REQUEST_CONFIG.edit,
        url: `${MEDICINE_REQUEST_CONFIG.edit.url}/${medicineId}`,
        data,
      }),
    {
      onSuccess: () => {
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateDeleteMedicine = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()
  return useMutation(
    (medicineId: number) =>
      cycleApi.request({
        ...MEDICINE_REQUEST_CONFIG.delete,
        url: `${MEDICINE_REQUEST_CONFIG.delete.url}/${medicineId}`,
      }),
    {
      onSuccess: () => {
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateCreateProductMedical = () => {
  const cycleApi = useCycleApi()

  return useMutation(({ body: data }: CreateProductMedicalVariables) =>
    cycleApi.request<CreateProductMessageResponse>({
      ...MEDICINE_REQUEST_CONFIG.createProduct,
      data,
    })
  )
}

export const useMutateCreateMedicineByProduct = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()
  return useMutation(
    ({ body: data }: CreateMedicineByProductVariables) =>
      cycleApi.request({ ...MEDICINE_REQUEST_CONFIG.createMedicineByProduct, data }),
    {
      onSuccess: () => {
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateUpdateMedicineByProduct = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()
  return useMutation(
    ({ medicineId, body: data }: EditProductMedicineVariables) =>
      cycleApi.request({
        ...MEDICINE_REQUEST_CONFIG.edit,
        url: `${MEDICINE_REQUEST_CONFIG.edit.url}/${medicineId}/product`,
        data,
      }),
    {
      onSuccess: () => {
        invalidateCostQueries()
      },
    }
  )
}

export const useMutateDeleteMedicineByProduct = () => {
  const queryClient = useQueryClient()
  const cycleApi = useCycleApi()
  const invalidateCostQueries = useInvalidateCostQueries()
  return useMutation(
    (medicineId: number) =>
      cycleApi.request({
        ...MEDICINE_REQUEST_CONFIG.delete,
        url: `${MEDICINE_REQUEST_CONFIG.delete.url}/${medicineId}/product`,
      }),
    {
      onSuccess: () => {
        invalidateCostQueries()
      },
    }
  )
}
